import { Injectable } from '@angular/core';

import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ReviewApiService, ReviewDeletedStatus } from '@vendasta/reputation';
import { TaskInterface } from '@vendasta/task';
import { BehaviorSubject, Observable, EMPTY as ObservableEmpty, of, throwError } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  flatMap,
  map,
  retry,
  shareReplay,
  skipWhile,
  switchMap,
  take,
  takeLast,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  ApproveConciergeResponseRequest,
  CanPublishReviewCommentData,
  CanPublishReviewCommentResponse,
  ConciergeReviewResponseData,
  ConciergeReviewResponseRequest,
  ConciergeReviewResponseResponse,
  CreateReviewCommentRequest,
  CreateReviewCommentResponse,
  ProvideConciergeFeedbackRequest,
  Review,
  ReviewComment,
  ReviewStatus,
  UpdateCallbackData,
  UpdateReviewCommentRequest,
  UpdateReviewCommentResponse,
  UpdateReviewRequest,
} from './review-types';

export const isNull = (val: any) => val === null;

@Injectable()
export class ReviewsService {
  protected task$$: BehaviorSubject<TaskInterface> = new BehaviorSubject<TaskInterface>(null);
  private search$$: BehaviorSubject<string> = new BehaviorSubject('');
  public updateCallback: (update: UpdateCallbackData) => void;
  constructor(
    protected readonly http: HttpClient,
    protected readonly snackbarService: SnackbarService,
    protected readonly translateService: TranslateService,
    protected readonly reviewApiService: ReviewApiService,
  ) {}
  get search$(): Observable<string> {
    return this.search$$.asObservable().pipe(debounceTime(200), distinctUntilChanged());
  }

  set task(task: TaskInterface) {
    this.task$$.next(task);
  }

  publishReview(review$: Observable<Review>, accountGroupId: string, partnerId: string): Observable<Review> {
    return review$.pipe(
      take(1),
      flatMap((r) => {
        return this.updateReviewAPI({
          accountGroupId: accountGroupId,
          partnerId: partnerId,
          reviewID: r.reviewID,
          isPublished: true,
        }).pipe(
          catchError((err) => {
            console.error(err);
            this.snackbarService.openErrorSnack(
              'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.ERROR_PUBLISHING_REVIEW',
            );
            return ObservableEmpty;
          }),
          withLatestFrom(review$),
          map(([, rvw]) => {
            this.snackbarService.openSuccessSnack(
              'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.REVIEW_PUBLISHED',
            );
            return { ...rvw, isPublished: true };
          }),
        );
      }),
    );
  }

  unpublishReview(review$: Observable<Review>, accountGroupId: string, partnerId: string): Observable<Review> {
    return review$.pipe(
      take(1),
      flatMap((r) => {
        return this.updateReviewAPI({
          accountGroupId: accountGroupId,
          partnerId: partnerId,
          reviewID: r.reviewID,
          isPublished: false,
        }).pipe(
          catchError((err) => {
            console.error(err);
            this.snackbarService.openErrorSnack(
              'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.ERROR_UNPUBLISHING_REVIEW',
            );
            return ObservableEmpty;
          }),
          withLatestFrom(review$),
          map(([, rvw]) => {
            this.snackbarService.openSuccessSnack(
              'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.REVIEW_UNPUBLISHED',
            );
            return { ...rvw, isPublished: false };
          }),
        );
      }),
    );
  }

  publishToReviewWidget(review$: Observable<Review>, accountGroupId: string, partnerId: string): Observable<Review> {
    return review$.pipe(
      take(1),
      flatMap((r) => {
        return this.updateReviewAPI({
          accountGroupId: accountGroupId,
          partnerId: partnerId,
          reviewID: r.reviewID,
          isPublishedThirdPartyReview: true,
        }).pipe(
          catchError((err) => {
            console.error(err);
            this.snackbarService.openErrorSnack(
              'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.ERROR_PUBLISHING_REVIEW_TO_WIDGET',
            );
            return ObservableEmpty;
          }),
          withLatestFrom(review$),
          map(([, rvw]) => {
            this.snackbarService.openSuccessSnack(
              'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.REVIEW_PUBLISHED_TO_WIDGET',
            );
            return { ...rvw, publishThirdPartyReview: true };
          }),
        );
      }),
    );
  }

  unpublishFromReviewWidget(
    review$: Observable<Review>,
    accountGroupId: string,
    partnerId: string,
  ): Observable<Review> {
    return review$.pipe(
      take(1),
      flatMap((r) => {
        return this.updateReviewAPI({
          accountGroupId: accountGroupId,
          partnerId: partnerId,
          reviewID: r.reviewID,
          isPublishedThirdPartyReview: false,
        }).pipe(
          catchError((err) => {
            console.error(err);
            this.snackbarService.openErrorSnack(
              'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.ERROR_UNPUBLISHING_REVIEW_FROM_WIDGET',
            );
            return ObservableEmpty;
          }),
          withLatestFrom(review$),
          map(([, rvw]) => {
            this.snackbarService.openSuccessSnack(
              'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.REVIEW_UNPUBLISHED_FROM_WIDGET',
            );
            return { ...rvw, publishThirdPartyReview: false };
          }),
        );
      }),
    );
  }

  updateReviewStatus(
    review$: Observable<Review>,
    status: ReviewStatus,
    accountGroupId: string,
    partnerId: string,
  ): Observable<Review> {
    return review$.pipe(
      take(1),
      flatMap((r) => {
        return this.updateReviewAPI({
          accountGroupId: accountGroupId,
          partnerId: partnerId,
          reviewID: r.reviewID,
          status,
        }).pipe(
          catchError((err) => {
            console.error(err);
            this.snackbarService.openErrorSnack(
              'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.ERROR_UPDATING_REVIEW_STATUS',
            );
            return ObservableEmpty;
          }),
          withLatestFrom(review$),
          map(([, rvw]) => {
            this.snackbarService.openSuccessSnack(
              'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.REVIEW_STATUS_UPDATED',
            );
            return { ...rvw, status: status };
          }),
        );
      }),
    );
  }

  createReviewComment(
    review$: Observable<Review>,
    commentText: string,
    accountGroupId: string,
    companyName: string,
  ): Observable<Review> {
    return review$.pipe(
      take(1),
      flatMap((r) => {
        return this.canPublishReviewCommentToSourceAPI(r.reviewID, accountGroupId)
          .pipe(
            skipWhile(isNull),
            takeLast(1),
            switchMap((res) => {
              if (res && !res.canPublish) {
                this.snackbarService.openErrorSnack(
                  res.message && res.message.length > 0
                    ? res.message
                    : this.translateService.instant(
                        'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.ERROR_ADDING_COMMENT',
                      ),
                );
                return ObservableEmpty;
              }
              return this.createReviewCommentAPI({
                accountGroupId: accountGroupId,
                reviewID: r.reviewID,
                commentText,
                companyName: companyName,
              });
            }),
            catchError((err) => {
              console.error(err);
              this.snackbarService.openErrorSnack(
                'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.ERROR_ADDING_COMMENT',
              );
              return ObservableEmpty;
            }),
          )
          .pipe(
            withLatestFrom(review$),
            map(([newComment, rvw]) => {
              // Update review status
              const comments = [...rvw.comments, newComment];
              const status = newComment.postedByOwner
                ? ReviewStatus.OWNER_RESPONDED
                : ReviewStatus.DIGITAL_AGENT_RESPONDED;
              this.snackbarService.openSuccessSnack(
                'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.COMMENT_SAVED',
              );
              return { ...rvw, status: status, comments: comments };
            }),
          );
      }),
    );
  }

  editReviewComment(
    comment: ReviewComment,
    updatedCommentText: string,
    accountGroupId: string,
  ): Observable<ReviewComment> {
    return this.canPublishReviewCommentToSourceAPI(comment.reviewID, accountGroupId).pipe(
      skipWhile(isNull),
      takeLast(1),
      switchMap((res) => {
        if (res && !res.canPublish) {
          this.snackbarService.openErrorSnack(
            res.message && res.message.length > 0
              ? res.message
              : this.translateService.instant(
                  'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.ERROR_EDITING_COMMENT',
                ),
          );
          return ObservableEmpty;
        }
        return this.updateReviewCommentAPI({
          accountGroupId: accountGroupId,
          reviewCommentId: comment.commentId,
          updatedCommentText: updatedCommentText,
        });
      }),
      catchError((err) => {
        console.error(err);
        this.snackbarService.openErrorSnack(
          'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.ERROR_EDITING_COMMENT',
        );
        return ObservableEmpty;
      }),
      tap((updatedComment: ReviewComment) => {
        Object.assign(comment, updatedComment);
        this.snackbarService.openSuccessSnack('COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.COMMENT_EDITED');
      }),
      shareReplay(),
    );
  }

  createConciergeFeedback(
    reviewId: string,
    feedbackText: string,
    accountGroupId: string,
    partnerId: string,
    callback?: () => void,
  ): void {
    this.provideConciergeFeedbackAPI({
      accountGroupId: accountGroupId,
      reviewId: reviewId,
      feedback: feedbackText,
      partnerId: partnerId,
    })
      .pipe(
        tap(() => {
          this.snackbarService.openSuccessSnack(
            'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.FEEDBACK_SAVED',
          );
          if (callback) {
            callback();
          }
        }),
        catchError((err) => {
          console.error(err);
          this.snackbarService.openErrorSnack(
            'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.ERROR_ADDING_FEEDBACK',
          );
          return ObservableEmpty;
        }),
      )
      .subscribe();
  }

  approveConciergeResponse(reviewId: string, accountGroupId: string, partnerId: string, callback?: () => void): void {
    this.approveConciergeResponseAPI({
      accountGroupId: accountGroupId,
      reviewId: reviewId,
      partnerId: partnerId,
    })
      .pipe(
        tap(() => {
          this.snackbarService.openSuccessSnack(
            'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.RESPONSE_APPROVED',
          );
          if (callback) {
            callback();
          }
        }),
        catchError((err) => {
          console.error(err);
          this.snackbarService.openErrorSnack(
            'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.ERROR_APPROVING_FEEDBACK',
          );
          return ObservableEmpty;
        }),
      )
      .subscribe();
  }

  getConciergeReviewResponse(
    reviewId: string,
    accountGroupId: string,
    partnerId: string,
  ): Observable<ConciergeReviewResponseData> {
    return this.getConciergeReviewResponseAPI({
      accountGroupId: accountGroupId,
      reviewId: reviewId,
      partnerId: partnerId,
    });
  }

  updateSearch(term: string): void {
    this.search$$.next(term);
  }

  updateReviewUserDeletedInfo(
    review$: Observable<Review>,
    userId: string,
    deletedStatus: ReviewDeletedStatus,
  ): Observable<Review> {
    return review$.pipe(
      take(1),
      flatMap((review) =>
        this.reviewApiService
          .updateReviewUserDeletedInfo({
            reviewId: review.reviewID,
            userId: userId,
            deletedStatus: deletedStatus,
          })
          .pipe(
            catchError((err) => {
              console.error(err);
              this.snackbarService.openErrorSnack(
                deletedStatus === ReviewDeletedStatus.REVIEW_DELETED_STATUS_USER_REMOVED
                  ? 'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.DELETED.ERROR_REMOVED'
                  : 'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.DELETED.ERROR_KEPT',
              );
              return ObservableEmpty;
            }),
            withLatestFrom(review$),
            map(([, rvw]) => {
              this.snackbarService.openSuccessSnack(
                deletedStatus === ReviewDeletedStatus.REVIEW_DELETED_STATUS_USER_REMOVED
                  ? 'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.DELETED.SUCCESSFULLY_REMOVED'
                  : 'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.DELETED.SUCCESSFULLY_KEPT',
              );
              return { ...rvw, deletedStatus: deletedStatus };
            }),
          ),
      ),
    );
  }

  getConciergeReviewResponseAPI(req: ConciergeReviewResponseRequest): Observable<ConciergeReviewResponseData> {
    let params = new HttpParams().set('reviewId', req.reviewId);
    params = params.append('partnerId', req.partnerId);
    return this.http
      .get<ConciergeReviewResponseResponse>(`account/${req.accountGroupId}/ajax/v1/review/getReviewResponse/`, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        params: params,
      })
      .pipe(map((res) => res.data));
  }

  approveConciergeResponseAPI(req: ApproveConciergeResponseRequest): Observable<any> {
    let params = new HttpParams().set('reviewId', req.reviewId);
    params = params.append('partnerId', req.partnerId);
    return this.http.post(`account/${req.accountGroupId}/ajax/v1/review/response/approve/`, null, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      params: params,
    });
  }

  provideConciergeFeedbackAPI(req: ProvideConciergeFeedbackRequest): Observable<any> {
    let params = new HttpParams().set('reviewId', req.reviewId);
    params = params.append('partnerId', req.partnerId);
    params = params.append('feedback', '');
    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    return this.http.post(
      `account/${req.accountGroupId}/ajax/v1/review/response/feedback/`,
      params.toString() + encodeURIComponent(req.feedback),
      {
        headers,
      },
    );
  }

  updateReviewAPI(req: UpdateReviewRequest): Observable<boolean> {
    let body = new HttpParams();
    body = body.set('partnerId', req.partnerId);
    body = body.set('accountGroupId', req.accountGroupId);
    body = body.set('reviewId', req.reviewID);
    if (req.status) {
      body = body.set('actionStatus', req.status);
    }
    if (req.isPublished !== undefined && req.isPublished !== null) {
      body = body.set('isPublishedFlag', `${req.isPublished}`);
    }
    if (req.isPublishedThirdPartyReview !== undefined && req.isPublishedThirdPartyReview !== null) {
      body = body.set('isPublishedThirdPartyReviewFlag', `${req.isPublishedThirdPartyReview}`);
    }
    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    return this.http
      .post<any>(`account/${req.accountGroupId}/ajax/v1/review/update/`, body.toString(), {
        headers,
      })
      .pipe(map(Boolean));
  }

  updateReviewCommentAPI(req: UpdateReviewCommentRequest): Observable<ReviewComment> {
    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    const body = new HttpParams().set('reviewCommentId', req.reviewCommentId).set('updatedCommentText', '');
    return this.http
      .post<{ data: UpdateReviewCommentResponse }>(
        `account/${req.accountGroupId}/ajax/v1/review/updateComment/`,
        body.toString() + encodeURIComponent(req.updatedCommentText),
        {
          headers,
        },
      )
      .pipe(
        map((res) => res.data),
        map((res) => ({
          commentText: res.commentText,
          created: new Date(res.createdDateTime),
          commentId: res.commentId,
          commentAuthor: res.commenterName,
          postedByOwner: res.postedByOwner,
          reviewID: res.rid,
        })),
      );
  }

  createReviewCommentAPI(req: CreateReviewCommentRequest): Observable<ReviewComment> {
    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    const body = new HttpParams()
      .set('reviewId', req.reviewID)
      .set('responseContent', req.commentText.replace(';', ''));
    return this.http
      .post<{ data: CreateReviewCommentResponse }>(
        `account/${req.accountGroupId}/ajax/v1/review/postComment/`,
        body.toString(),
        {
          headers,
        },
      )
      .pipe(
        map((res) => res.data),
        map((res) => ({
          commentText: res.commentText,
          created: new Date(res.createdDateTime),
          commentId: res.commentId,
          commentAuthor: res.commenterName,
          postedByOwner: res.postedByOwner,
          reviewID: req.reviewID,
        })),
      );
  }

  canPublishReviewCommentToSourceAPI(
    reviewID: string,
    accountGroupId: string,
  ): Observable<CanPublishReviewCommentData> {
    const params = new HttpParams().set('reviewId', reviewID);
    return this.http
      .get<CanPublishReviewCommentResponse>(`account/${accountGroupId}/ajax/v1/review/canComment/`, { params: params })
      .pipe(
        map((res) => res.data),
        retry(2),
      );
  }

  suggestReviewResponse(reviewId: string, accountGroupId: string, isSameLanguage: boolean): Observable<string> {
    return this.reviewApiService
      .suggestReviewResponse({
        reviewId: reviewId,
        businessId: accountGroupId,
        inSameLanguage: isSameLanguage,
      })
      .pipe(map((res) => res.message));
  }

  handleRequestApproval(
    reviewId: string,
    partnerId: string,
    task: TaskInterface,
    accountGroupId: string,
    existingResponse$: Observable<ConciergeReviewResponseData>,
    commentText: string,
  ): Observable<number> {
    return this.requestApproval(reviewId, partnerId, task, accountGroupId, existingResponse$, commentText).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.snackbarService.openErrorSnack(
            'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.REVIEW_NO_LONGER_EXISTS',
          );
        } else {
          this.snackbarService.openErrorSnack(
            'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.ERROR_REQUESTING_APPROVAL',
          );
        }
        return throwError(err);
      }),
      tap(() =>
        this.snackbarService.openSuccessSnack(
          'COMMON_REVIEW_CARD.REVIEWS.MANAGE.REVIEW_CARD.MESSAGES.APPROVAL_REQUESTED',
        ),
      ),
      take(1),
    );
  }

  requestApproval(
    reviewId: string,
    partnerId: string,
    task: TaskInterface,
    accountGroupId: string,
    existingResponse$: Observable<ConciergeReviewResponseData>,
    commentText: string,
  ): Observable<number> {
    if (reviewId || partnerId || task || accountGroupId || existingResponse$ || commentText) {
      //no-op
    }
    return of(null);
  }

  updateReviewDraft(
    reviewId: string,
    partnerId: string,
    task: TaskInterface,
    accountGroupId: string,
    existingResponse$: Observable<ConciergeReviewResponseData>,
    commentText: string,
  ): Observable<number> {
    if (reviewId || partnerId || task || accountGroupId || existingResponse$ || commentText) {
      //no-op
    }
    return of(null);
  }
}
